import api from '@/shared/api/api';
import {
  Collection,
  PaginatedCollectionsResponse,
  GetCollectionsParams,
  CreateCollectionPayload,
  UpdateCollectionPayload,
  CollectionResponse,
} from '../types';

const buildCollectionFormData = (
  payload: CreateCollectionPayload | UpdateCollectionPayload,
  isUpdate: boolean = false
): FormData => {
  const form = new FormData();
  
  // Construir o objeto data conforme o payload esperado
  const data: {
    collection?: {
      id?: string;
      name: string;
      translations?: Array<{ languageCode: string; description: string }>;
      items?: Array<{ itemId: string; displayOrder: number }>;
    };
    banner?: {
      id?: string;
      redirectType: string;
      isActive: boolean;
      startDate: string;
      endDate: string;
    };
  } = {};
  
  if ('collection' in payload && payload.collection) {
    data.collection = {
      name: payload.collection.name!,
    };
    
    // Incluir id apenas no PUT
    if (isUpdate && 'id' in payload.collection && payload.collection.id) {
      data.collection.id = payload.collection.id;
    }
    
    if (payload.collection.translations && payload.collection.translations.length > 0) {
      data.collection.translations = payload.collection.translations;
    }
    
    if (payload.collection.items && payload.collection.items.length > 0) {
      data.collection.items = payload.collection.items;
    }
  }
  
  if ('banner' in payload && payload.banner) {
    data.banner = {
      redirectType: payload.banner.redirectType!,
      isActive: payload.banner.isActive!,
      startDate: payload.banner.startDate!,
      endDate: payload.banner.endDate!,
    };
    
    // Incluir id apenas no PUT
    if (isUpdate && 'id' in payload.banner && payload.banner.id) {
      data.banner.id = payload.banner.id;
    }
  }
  
  // Adicionar o objeto data como JSON string
  form.append('data', JSON.stringify(data));
  
  // Adicionar arquivos separadamente
  if (payload.coverImage) {
    form.append('coverImage', payload.coverImage);
  }
  
  if (payload.bannerImage) {
    form.append('bannerImage', payload.bannerImage);
  }
  
  return form;
};

export const collectionApi = {
  list: async (
    params?: GetCollectionsParams,
    signal?: AbortSignal
  ): Promise<PaginatedCollectionsResponse> => {
    const mapped: Record<string, unknown> = {};
    const p = params ?? {};
    if (p.page !== undefined) mapped.page = p.page;
    if (p.limit !== undefined) mapped.limit = p.limit;
    if (p.isActive !== undefined) mapped.isActive = p.isActive;
    
    return await api<PaginatedCollectionsResponse>({
      url: '/v1/collections',
      method: 'GET',
      params: mapped,
      signal,
    });
  },

  get: async (id: string, signal?: AbortSignal): Promise<Collection> => {
    const response = await api<CollectionResponse>({
      url: `/v1/collections/${id}`,
      method: 'GET',
      signal,
    });
    
    // Mapear resposta da API para o formato interno
    return {
      id: response.collection.id,
      name: response.collection.name,
      description: '', // A API não retorna description no GET by ID, mas mantemos para compatibilidade
      cover: response.collection.coverImage?.url || '',
      translations: response.collection.translations?.map(t => ({
        languageCode: t.languageCode,
        description: t.description,
      })) || [],
      // Converter items da API para o formato interno
      // A nova API retorna { itemId, displayOrder, image, name }
      items: response.collection.items?.map(item => ({
        itemId: item.itemId,
        displayOrder: item.displayOrder,
        image: item.image,
        name: item.name,
      })) || [],
      banner: response.banner ? {
        id: response.banner.id,
        redirectType: response.banner.redirectType,
        isActive: response.banner.isActive,
        startDate: response.banner.startDate,
        endDate: response.banner.endDate,
      } : undefined,
      bannerImageUrl: response.banner?.bannerImage?.url,
      createdAt: response.collection.createdAt,
      updatedAt: response.collection.updatedAt,
    };
  },

  create: async (payload: CreateCollectionPayload): Promise<Collection> => {
    const form = buildCollectionFormData(payload, false);
    return api<Collection>({
      url: '/v1/collections',
      method: 'POST',
      data: form,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  update: async (id: string, payload: UpdateCollectionPayload): Promise<Collection> => {
    const form = buildCollectionFormData(payload, true);
    return api<Collection>({
      url: `/v1/collections/${id}`,
      method: 'PUT',
      data: form,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  remove: async (id: string): Promise<{ id: string; deletedAt: string }> => {
    return api({
      url: `/v1/collections/${id}`,
      method: 'DELETE',
    });
  },
};
